import { Box, IconButton, TableCell, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import ActionButton from "../../components/Button/ActionButton";
import ConfigurationSection from "../../components/payments/ConfigurationSection/ConfigurationSection";
import TableComponent, { StyledTableRow } from "../../components/Table/Table";
import { PaymentLine } from "../../hooks/useRequestPayment";
import { formatEUR } from "../../utils/money";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

const headerSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2
};

const headingSx = {
  ...typography.body.xlBold,
  color: semanticColors.text.primary
};

const emptySx = {
  ...typography.body.l,
  color: semanticColors.text.secondary
};

const totalSx = {
  ...typography.body.xlBold,
  color: semanticColors.text.primary,
  textAlign: "right"
};

const removeIconSx = { color: semanticColors.status.error };

/**
 * The amount is one unit: the column is narrow enough that the currency symbol
 * would otherwise drop to its own line, and the description column is the one
 * that should wrap.
 */
const nowrapSx = { whiteSpace: "nowrap" };

/** The lines being charged, with their running total. */
export default function PaymentLinesSection({
  lines,
  total,
  onAddLine,
  onRemoveLine
}: {
  lines: PaymentLine[];
  total: number;
  onAddLine: () => void;
  onRemoveLine: (line: PaymentLine) => void;
}) {
  const { t } = useTranslation();

  return (
    <ConfigurationSection>
      <Box sx={headerSx}>
        <Typography sx={headingSx}>{t("requestPayment.lines")}</Typography>
        <ActionButton label={t("requestPayment.addLine")} onClick={onAddLine} />
      </Box>

      {lines.length === 0 && (
        <Typography sx={emptySx}>{t("requestPayment.noEntries")}</Typography>
      )}

      {lines.length > 0 && (
        <TableComponent
          headerCfg={[
            { label: t("requestPayment.procedure"), align: "left" },
            { label: t("requestPayment.quantity") },
            // Both titles carry a unit and read as one phrase; the procedure
            // column has the free text and takes the slack.
            { label: t("requestPayment.unitCost"), sx: nowrapSx },
            { label: "", size: "small" }
          ]}
        >
          {lines.map(line => (
            <StyledTableRow key={line.id}>
              <TableCell align="left">{line.description}</TableCell>
              <TableCell align="center">{line.quantity}</TableCell>
              <TableCell align="center" sx={nowrapSx}>
                {formatEUR(line.unitCost)}
              </TableCell>
              <TableCell align="center" size="small">
                <IconButton
                  onClick={() => onRemoveLine(line)}
                  aria-label={t("requestPayment.removeLine")}
                >
                  <DeleteOutlineIcon sx={removeIconSx} />
                </IconButton>
              </TableCell>
            </StyledTableRow>
          ))}
        </TableComponent>
      )}

      <Typography sx={totalSx}>
        {t("requestPayment.total", { value: formatEUR(total) })}
      </Typography>
    </ConfigurationSection>
  );
}
